import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import {
  AuthorisationFlowType,
  ConfirmationPreferenceTypes,
  CredentialType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { AvailableConfirmationMethodsUpdate } from '../../actions/user-profile.action';
import { ApiActions, ApiLoadingState } from '../../state/api-loading.state';
import { ProjectSettingsState, ProjectSettingsStateModel } from '../../state/project-settings.state';
import { KeycloakCustomService } from '../keycloak-wrapper-service/keycloak-custom.service';
import { CredentialTypeMetadata } from '../keycloak-wrapper-service/keycloak-custom.type';

@Injectable({ providedIn: 'root' })
export class ConfirmationService {
  private projectSettings: ProjectSettingsStateModel;
  private usersCredentials: CredentialTypeMetadata[];
  private confirmationPreferenceOrder: ConfirmationPreferenceTypes[];
  constructor(
    private store: Store,
    private router: Router,
    private keycloakCustomService: KeycloakCustomService,
  ) {
    this.projectSettings = this.store.selectSnapshot(ProjectSettingsState.getProjectSettings);
  }

  //map these into credential types to be able to compare them
  public async getUsersCredentials() {
    await this.keycloakCustomService.getUserCredentialOptions().then((usersCreds) => {
      this.usersCredentials = usersCreds;
    });
  }

  public getProjectPreferenceOrder() {
    return (
      this.projectSettings.authentication?.[AuthorisationFlowType.ALL_OTHER_FLOWS]
        .confirmationPreferenceOrder ??
      this.projectSettings.confirmationPreferenceOrder ??
      []
    );
  }

  private async mapKeycloakCredsToConfirmationTypes() {
    const usersCreds: ConfirmationPreferenceTypes[] = [
      ConfirmationPreferenceTypes.SMS_OTP,
      ConfirmationPreferenceTypes.EMAIL_OTP,
    ];
    await this.getUsersCredentials();
    this.usersCredentials.forEach((credential) => {
      switch (credential.type) {
        case 'biometric':
          usersCreds.push(ConfirmationPreferenceTypes.BIOMETRICS);
          break;
        case 'pin':
          usersCreds.push(ConfirmationPreferenceTypes.APP_PIN);
          break;
        case 'otp':
          if (credential.userCredentialMetadatas.length > 0) {
            usersCreds.push(ConfirmationPreferenceTypes.TOTP);
          }
          break;
        default:
          break;
      }
    });
    return usersCreds;
  }
  private async getAvailablePreferenceOrder(
    customPreferenceOrder?: ConfirmationPreferenceTypes[],
    linkedToProjectOrder?: boolean,
  ) {
    const usersConfirmationTypes = await this.mapKeycloakCredsToConfirmationTypes();
    let preferenceOrder;
    if (!linkedToProjectOrder && customPreferenceOrder) {
      preferenceOrder = customPreferenceOrder;
    } else {
      preferenceOrder = this.getProjectPreferenceOrder();
    }
    const usersPreferenceOrder: ConfirmationPreferenceTypes[] = [];
    preferenceOrder.forEach((preference) => {
      if (usersConfirmationTypes.includes(preference)) {
        usersPreferenceOrder.push(preference);
      }
    });
    this.confirmationPreferenceOrder = usersPreferenceOrder;
    this.store.dispatch(new AvailableConfirmationMethodsUpdate(Array.from(new Set(usersPreferenceOrder))));
  }

  /**
   * Resolves which credential type the user should confirm with, honouring the
   * project/user confirmation preference order (and dispatching
   * AvailableConfirmationMethodsUpdate as a side effect). Shared by the routed
   * /confirmation-code flow (confirmAction) and the overlay-hosted flow
   * (OverlayConfirmationService).
   */
  public async resolvePreferredCredentialType(
    customPreferenceOrder?: ConfirmationPreferenceTypes[],
    linkedToProjectOrder?: boolean,
  ): Promise<CredentialType> {
    await this.getAvailablePreferenceOrder(customPreferenceOrder, linkedToProjectOrder);
    return this.getClaimTypeFromPreferenceType(this.confirmationPreferenceOrder[0]);
  }

  public async confirmAction(
    claimType: string,
    returnUrl = '',
    addTokenToUrl = false,
    loadingAction?: ApiActions,
    customPreferenceOrder?: ConfirmationPreferenceTypes[],
    linkedToProjectOrder?: boolean,
    failureNavigationUrl?: string,
  ) {
    const credentialType = await this.resolvePreferredCredentialType(
      customPreferenceOrder,
      linkedToProjectOrder,
    );
    this.router.navigate(['confirmation-code'], {
      queryParams: {
        claimType,
        confirmationType: credentialType,
        returnUrl,
        addTokenToUrl,
        loadingAction,
        failureNavigationUrl,
      },
    });
  }

  public checkConfirmation(targetUrl?: string, loadingAction?: ApiActions): Observable<void> {
    return new Observable((observer) => {
      const subscriptions: Subscription[] = [];

      if (loadingAction) {
        const loadingSubscription = this.store
          .select(ApiLoadingState.getApiState(loadingAction))
          .subscribe((action) => {
            if (action.isLoading) {
              observer.next();
              observer.complete();
            }
          });
        subscriptions.push(loadingSubscription);
      }
      const routerSubscription = this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart && event.navigationTrigger === 'popstate') {
          // Back navigation detected, close the observable to stop multiple calls
          observer.complete();
        }

        if (
          !loadingAction &&
          targetUrl &&
          event instanceof NavigationEnd &&
          (event.url === targetUrl || event.url.includes(targetUrl))
        ) {
          observer.next();
          observer.complete();
        }
      });
      subscriptions.push(routerSubscription);
      // Cleanup logic when the observable is unsubscribed
      return () => subscriptions.forEach((sub) => sub.unsubscribe());
    });
  }

  private getClaimTypeFromPreferenceType(preferenceType: ConfirmationPreferenceTypes) {
    switch (preferenceType) {
      case ConfirmationPreferenceTypes.BIOMETRICS:
        return CredentialType.BIOMETRIC;
      case ConfirmationPreferenceTypes.APP_PIN:
        return CredentialType.PIN;
      case ConfirmationPreferenceTypes.SMS_OTP:
        return CredentialType.SMS;
      case ConfirmationPreferenceTypes.EMAIL_OTP:
        return CredentialType.EMAIL;
      case ConfirmationPreferenceTypes.TOTP:
        return CredentialType.TOTP;
      default:
        return CredentialType.PASSWORD;
    }
  }
}
